import { isAccountant, isAdmin, isProjectManagerOver, sanitizeWoNumber } from "src/Common/function";
import api from "./api";
import UserService from "./user.service";

const getAllWorkOrders = (userId, keyword, pageNumber, criteria, sort, branch, limit, offset) => {
  let currentUser = UserService.getCurrentUser();
  let branchId = branch;
  // if (isProjectManagerOver() || isAccountant()) {
  //   branchId = branch;
  // } else {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/wo/get_all", {
    params: { userId, branchId, keyword, pageNumber, criteria, sort, limit, offset },
  });
};

// Work orders for a single customer/location, used by the customer & location
// detail pages. Hits a customer:read-gated endpoint so viewing a customer does
// not require global work-order (wo:read) access.
const getWorkOrdersByLocation = (userId, keyword, pageNumber, criteria, sort, branch) => {
  let branchId = branch;
  return api.get("/api/wo/get_by_location", {
    params: { userId, branchId, keyword, pageNumber, criteria, sort },
  });
};

// Lightweight WO picker for the "Assign form to work order" dropdown. Hits an
// ungated endpoint so assigning a form (form:assign) doesn't require wo:read.
// Returns { orders: [{ id, number, description }] }.
const getWorkOrdersForAssign = (keyword, branch) => {
  return api.get("/api/wo/get_for_form_assign", {
    params: { keyword, branchId: branch },
  });
};

const getAllOrderNumber = (branch) => {
  let branchId = branch;
  // let currentUser = UserService.getCurrentUser();
  // if (!isAdmin()) {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/wo/getAllOrderNumber", { params: { branchId } });
};

const getAllRecommendations = (keyword, pageNumber, branch, limit, offset) => {
  let currentUser = UserService.getCurrentUser();
  let branchId = branch;
  // if (!isAdmin()) {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/wo/get_recommendations", { params: { keyword, pageNumber, branchId, userId: currentUser.id, limit, offset } });
};

const createWorkOrder = (data) => {
  return api.post("/api/wo/create_wo", data);
};

const getWorkOrderById = (id) => {
  return api.get("/api/wo/getById", { params: { id } });
};

const getWorkOrderByNumber = (number) => {
  return api.get("/api/wo/getByNumber", { params: { number } });
};

const update = (data) => {
  return api.post("/api/wo/update", data);
};

// Approving a requested work order. Hits a dedicated wo:approve-gated endpoint
// (distinct from wo:update) so approval can be granted independently of editing.
const approve = (data) => {
  return api.post("/api/wo/approve", data);
};

// Recording a billed date. Hits a dedicated wo:addDateBilled-gated endpoint
// (distinct from wo:update) so billing can be granted independently of editing.
const setBilledDate = (data) => {
  return api.post("/api/wo/set_billed_date", data);
};

const uploadFiles = (files, orderId, type, currentUser, metadata, onUploadProgress) => {
  const formData = new FormData();
  formData.append("orderId", orderId);
  formData.append("type", type);
  formData.append("userId", currentUser.id);
  for (let file of files) {
    formData.append("files", file);
  }
  // Per-file metadata (e.g. attachmentType/documentType/includeInServiceTicket),
  // aligned by index with the appended files. Parsed server-side via JSON.parse.
  if (metadata) {
    formData.append("metadata", JSON.stringify(metadata));
  }

  return api.post("/api/wo/attach", formData, {
    headers: {
      "content-type": "multipart/form-data",
    },
    // Optional callback so callers can drive an upload progress bar.
    onUploadProgress,
  });
};

const updateAttachment = (data) => {
  return api.post("/api/wo/update_attachment", data);
};

// Staff editing of a work order's service ticket (materials). Hits dedicated
// wo:updateServiceTicket-gated _staff endpoints; the base attach_materials /
// update_material endpoints stay open for the technician app, which fills the
// service ticket in the field without staff permissions.
const attachMaterials = (data) => {
  return api.post("/api/wo/attach_materials_staff", data);
};

const updateMaterial = (woID, index, data, eventUser) => {
  return api.post("/api/wo/update_material_staff", { woID, index, data, eventUser });
};

const previewServiceTicket = (woId) => {
  return api.get("/api/wo/preview_serviceticket", { params: { id: woId } });
};

// Staff sending of the service ticket to the customer. Hits a dedicated
// wo:sendServiceTicket-gated _staff endpoint; the base send_serviceticket stays
// open for the technician app, which also sends tickets from the field.
const sendServiceTicket = (woId) => {
  return api.get("/api/wo/send_serviceticket_staff", {
    params: { id: woId, stSentDate: new Date() },
  });
};

const deleteById = (id) => {
  return api.get("/api/wo/deleteByID", { params: { id } });
};

// Staff attachment delete. Hits the dedicated wo:deleteAttachment-gated staff
// endpoint (the shared /delete_attachment stays ungated for the tech + customer
// apps, which delete their own attachments without staff permissions).
const deleteAttachment = (woId, attachmentId, type) => {
  return api.get("/api/wo/delete_attachment_staff", { params: { woId, attachmentId, type } });
};

// Staff proposal delete. Proposals are stored as attachments (type 3) but
// deletion is gated with wo:deleteProposal, so it hits a dedicated endpoint
// separate from generic attachment deletion.
const deleteProposal = (woId, attachmentId, type) => {
  return api.get("/api/wo/delete_proposal_staff", { params: { woId, attachmentId, type } });
};

// Staff invoice delete. Invoices are stored as attachments (type 4) but deletion
// is gated with wo:deleteInvoice, so it hits a dedicated endpoint separate from
// generic attachment deletion.
const deleteInvoice = (woId, attachmentId, type) => {
  return api.get("/api/wo/delete_invoice_staff", { params: { woId, attachmentId, type } });
};

// Staff form-file delete. Uploaded form files (from the technician app) are
// stored as attachments (type 2) but deletion is gated with wo:deleteForm, so it
// hits a dedicated endpoint separate from generic attachment deletion.
const deleteForm = (woId, attachmentId, type) => {
  return api.get("/api/wo/delete_form_staff", { params: { woId, attachmentId, type } });
};

// Staff technical-document delete. Technical documents are stored as attachments
// (type 0) but deletion is gated with wo:deleteTechnicalDocument, so it hits a
// dedicated endpoint separate from generic attachment deletion.
const deleteTechnicalDocument = (woId, attachmentId, type) => {
  return api.get("/api/wo/delete_technical_document_staff", { params: { woId, attachmentId, type } });
};

const generateExcelFile = (userId, keyword, criteria, sort, branch) => {
  let branchId = branch;
  // let currentUser = UserService.getCurrentUser();
  // if (!isAdmin()) {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/wo/generate_excel_file", { params: { userId, branchId, keyword, criteria, sort } });
};

const getAllOrderNumberWithKeyword = (keyword, branch, config = {}) => {
  let branchId = branch;
  // let currentUser = UserService.getCurrentUser();
  // if (!isAdmin() && !isAccountant()) {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/wo/getAllOrderNumberWithKeyword", { params: { keyword, branchId }, ...config });
};

const getWOsForCalendar = (view, year, month, weekNumber, branch) => {
  return api.get("/api/wo/get_wos_for_calendar", { params: { view, year, month, weekNumber, branch } });
};

const getWOsForCalendarByTechnician = (view, year, month, weekNumber, branch, userId) => {
  return api.get("/api/wo/get_wos_for_calendar_by_technician", {
    params: { view, year, month, weekNumber, branch, userId },
  });
};

const getAssignedTechniciansByWoId = (woId) => {
  return api.get("/api/public/wo/assigned-technicians", { params: { woId } });
};

const checkWONumberDuplicate = (number) => {
  return api.get("/api/wo/check_wo_number_duplicate", { params: { number: sanitizeWoNumber(number) } });
};

const WOService = {
  checkWONumberDuplicate,
  createWorkOrder,
  getAllWorkOrders,
  getWorkOrdersByLocation,
  getWorkOrdersForAssign,
  sendServiceTicket,
  previewServiceTicket,
  attachMaterials,
  getWorkOrderById,
  getWorkOrderByNumber,
  uploadFiles,
  update,
  approve,
  setBilledDate,
  deleteById,
  deleteProposal,
  deleteInvoice,
  deleteForm,
  deleteTechnicalDocument,
  updateMaterial,
  getAllRecommendations,
  getAllOrderNumber,
  deleteAttachment,
  generateExcelFile,
  getAllOrderNumberWithKeyword,
  getWOsForCalendar,
  getWOsForCalendarByTechnician,
  getAssignedTechniciansByWoId,
  updateAttachment,
};

export default WOService;
